import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { storageBaseUrl } from '../../api';

const StarFull = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fbbf24" stroke="#fbbf24" strokeWidth="1">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const StarEmpty = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const StarSmall = ({ filled }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? '#fbbf24' : 'none'} stroke={filled ? '#fbbf24' : '#d1d5db'} strokeWidth="1.5">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const MapPinIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const PhoneIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.99 13 19.79 19.79 0 0 1 1.93 4.35 2 2 0 0 1 3.89 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);

const AwardIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="6" />
        <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
    </svg>
);

const ArrowLeft = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const renderStars = (rating) => {
    const full = Math.floor(rating);
    const empty = 5 - full;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {Array.from({ length: full }).map((_, i) => <StarFull key={`f${i}`} />)}
            {Array.from({ length: empty }).map((_, i) => <StarEmpty key={`e${i}`} />)}
        </div>
    );
};

const renderSmallStars = (rating) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Array.from({ length: 5 }).map((_, i) => <StarSmall key={i} filled={i < rating} />)}
    </div>
);

const InfoRow = ({ label, icon, children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: '0.9rem', fontWeight: 500, paddingTop: 2 }}>
            {icon}{label}
        </div>
        <div style={{ color: '#1a1a2e', fontSize: '0.9rem', lineHeight: 1.6 }}>{children}</div>
    </div>
);

const ContractorDetailPage = () => {
    const { contractorId } = useParams();
    const navigate = useNavigate();
    const [contractor, setContractor] = useState(null);
    const [isSelected, setIsSelected] = useState(false);

    useEffect(() => {
        api.get(`contractors/${contractorId}`)
            .then(r => setContractor(r.data))
            .catch(console.error);
        const selected = JSON.parse(sessionStorage.getItem('selected_contractors') || '[]');
        setIsSelected(selected.includes(Number(contractorId)));
    }, [contractorId]);

    const handleSelect = () => {
        const selected = JSON.parse(sessionStorage.getItem('selected_contractors') || '[]');
        if (!selected.includes(Number(contractorId))) {
            selected.push(Number(contractorId));
            sessionStorage.setItem('selected_contractors', JSON.stringify(selected));
        }
        navigate('/customer/search');
    };

    if (!contractor) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const perf = contractor.performance;
    const reviews = contractor.reviews || [];
    const areaByPref = (contractor.service_areas || []).reduce((acc, city) => {
        const pref = city.prefecture?.name || '不明';
        if (!acc[pref]) acc[pref] = [];
        acc[pref].push(city.name);
        return acc;
    }, {});

    const cardStyle = {
        background: '#fff',
        borderRadius: 8,
        padding: '28px 32px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: 16,
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8', paddingBottom: 100 }}>
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 16px' }}>

                {/* 戻るボタン */}
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#005c53', fontWeight: 500, fontSize: '0.9rem',
                        padding: '0 0 20px 0',
                    }}>
                    <ArrowLeft /> 戻る
                </button>

                {/* ヘッダーカード */}
                <div style={cardStyle}>
                    <h1 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>
                        {contractor.company_name}
                    </h1>
                    {perf && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            {renderStars(perf.rating || 0)}
                            <span style={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
                                {perf.rating?.toFixed(1) ?? '-'}
                            </span>
                            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                ({reviews.length}件のレビュー)
                            </span>
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MapPinIcon />
                        <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                            {contractor.prefecture}{contractor.city}
                        </span>
                    </div>
                </div>

                {/* 基本情報 */}
                <div style={cardStyle}>
                    <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 20 }}>基本情報</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {contractor.representative && (
                            <InfoRow label="代表者">{contractor.representative}</InfoRow>
                        )}
                        <InfoRow label="所在地">
                            {contractor.prefecture}{contractor.city}{contractor.address}
                        </InfoRow>
                        {contractor.phone && (
                            <InfoRow label="電話番号" icon={<PhoneIcon />}>
                                {contractor.phone}
                            </InfoRow>
                        )}
                        {contractor.description && (
                            <InfoRow label="紹介文">{contractor.description}</InfoRow>
                        )}
                        {contractor.qualification_certificate && (
                            <InfoRow label="保有資格" icon={<AwardIcon />}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                                    {contractor.qualification_certificate.split('\n').filter(Boolean).map((q, i) => (
                                        <span key={i} style={{ padding: '3px 10px', background: '#e6f2f1', color: '#005c53', borderRadius: 20, fontSize: '0.85rem' }}>
                                            {q}
                                        </span>
                                    ))}
                                </div>
                            </InfoRow>
                        )}
                        {contractor.qualification_certificate_image && (
                            <InfoRow label="資格証">
                                <a
                                    href={`${storageBaseUrl}/${contractor.qualification_certificate_image}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src={`${storageBaseUrl}/${contractor.qualification_certificate_image}`}
                                        alt="資格証"
                                        style={{ maxWidth: '100%', width: 280, height: 'auto', borderRadius: 6, border: '1px solid #e0e4ea', cursor: 'pointer' }}
                                    />
                                </a>
                            </InfoRow>
                        )}
                    </div>
                </div>

                {/* 実績 */}
                {perf && (
                    <div style={cardStyle}>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 20 }}>実績</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, textAlign: 'center' }}>
                            {[
                                { val: perf.orders_received ?? '-', label: '受注件数' },
                                { val: perf.estimate_request ?? '-', label: '見積依頼数' },
                                { val: perf.rating?.toFixed(1) ?? '-', label: '平均評価' },
                            ].map((m, i) => (
                                <div key={i}>
                                    <div style={{ fontSize: '2.4rem', fontWeight: 700, color: '#005c53', lineHeight: 1, marginBottom: 6 }}>
                                        {m.val}
                                    </div>
                                    <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>{m.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 対応エリア */}
                {Object.keys(areaByPref).length > 0 && (
                    <div style={cardStyle}>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 20 }}>対応エリア</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {Object.entries(areaByPref).map(([pref, cities]) => (
                                <div key={pref}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 6 }}>{pref}</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {cities.map((city, i) => (
                                            <span key={i} style={{
                                                padding: '4px 12px',
                                                background: '#e6f2f1',
                                                color: '#005c53',
                                                borderRadius: 20,
                                                fontSize: '0.82rem',
                                                fontWeight: 500,
                                            }}>
                                                {city}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* レビュー */}
                <div style={{ ...cardStyle, marginBottom: 0 }}>
                    <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 20 }}>
                        レビュー（{reviews.length}件）
                    </h2>
                    {reviews.length === 0 ? (
                        <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>まだレビューはありません。</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {reviews.map((r, idx) => (
                                <div key={r.id} style={{
                                    paddingBottom: 20,
                                    marginBottom: 20,
                                    borderBottom: idx < reviews.length - 1 ? '1px solid #e0e4ea' : 'none',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.9rem' }}>
                                                {r.customer_name}
                                            </span>
                                            {renderSmallStars(r.rating)}
                                        </div>
                                    </div>
                                    {r.comment && (
                                        <p style={{ margin: 0, color: '#1a1a2e', fontSize: '0.9rem', lineHeight: 1.6 }}>
                                            {r.comment}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 固定フッターバー */}
            <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0,
                background: '#fff', borderTop: '1px solid #e0e4ea',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
                zIndex: 50,
            }}>
                <div style={{ maxWidth: 860, margin: '0 auto', padding: '14px 16px' }}>
                    {isSelected ? (
                        <button disabled style={{
                            width: '100%', padding: '14px',
                            background: '#e8f5e9', color: '#2e7d32',
                            border: '1px solid #a5d6a7', borderRadius: 4,
                            fontWeight: 600, fontSize: '1rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            cursor: 'default',
                        }}>
                            <CheckIcon /> この業者は選択中です
                        </button>
                    ) : (
                        <button
                            onClick={handleSelect}
                            style={{
                                width: '100%', padding: '14px',
                                background: '#005c53', color: '#fff',
                                border: 'none', borderRadius: 4,
                                fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
                            }}>
                            この業者を選択する
                        </button>
                    )}
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ContractorDetailPage;
